import { useEffect, useState } from 'react'
import { Gift, Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { fetchLoyaltySettings, updateLoyaltySettings } from '@/lib/api/loyaltySettings'
import { cn } from '@/lib/utils'

import { SettingsSection } from './SettingsSection'

const inputClass = cn(
  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
  'focus:outline-none focus:ring-2 focus:ring-ring',
)

function emptyForm() {
  return {
    enabled: false,
    earn_rate: '1',
    redeem_rate: '0.10',
    min_redeem_points: '100',
  }
}

/**
 * @param {import('@/lib/api/loyaltySettings').LoyaltySettings} settings
 */
function settingsToForm(settings) {
  return {
    enabled: Boolean(settings.enabled),
    earn_rate: String(settings.earn_rate ?? 1),
    redeem_rate: String(settings.redeem_rate ?? 0.1),
    min_redeem_points: String(settings.min_redeem_points ?? 100),
  }
}

export function LoyaltySettingsSection({ embedded = false }) {
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [form, setForm] = useState(emptyForm)

  useEffect(() => {
    let active = true
    setLoading(true)
    setError('')

    fetchLoyaltySettings()
      .then((settings) => {
        if (active) {
          setForm(settingsToForm(settings))
        }
      })
      .catch((err) => {
        if (active) {
          setError(err?.response?.data?.message ?? 'Unable to load loyalty settings.')
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false)
        }
      })

    return () => {
      active = false
    }
  }, [])

  /**
   * @param {string} key
   * @param {string | boolean} value
   */
  function updateField(key, value) {
    setForm((current) => ({ ...current, [key]: value }))
    setSuccess('')
  }

  async function handleSubmit(event) {
    event.preventDefault()
    setSubmitting(true)
    setError('')
    setSuccess('')

    try {
      const updated = await updateLoyaltySettings({
        enabled: form.enabled,
        earn_rate: Number(form.earn_rate),
        redeem_rate: Number(form.redeem_rate),
        min_redeem_points: Number(form.min_redeem_points),
      })
      setForm(settingsToForm(updated))
      setSuccess('Loyalty settings saved.')
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to save loyalty settings.')
    } finally {
      setSubmitting(false)
    }
  }

  const earnPreview = Number(form.earn_rate)
  const redeemPreview = Number(form.redeem_rate)
  const minPreview = Number(form.min_redeem_points)

  return (
    <SettingsSection
      id="loyalty-settings"
      icon={Gift}
      title="Loyalty points"
      description="Reward guests with points on each paid bill and let them redeem at checkout."
      embedded={embedded}
    >
      {loading ? (
        <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Loading…
        </div>
      ) : (
        <form className="space-y-4" onSubmit={handleSubmit}>
          <label className="flex items-center gap-3 rounded-xl border border-border/60 bg-muted/30 px-4 py-3">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(event) => updateField('enabled', event.target.checked)}
              className="h-4 w-4 rounded border-border"
            />
            <span className="text-sm font-medium">Enable loyalty program</span>
          </label>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block space-y-1.5">
              <span className="text-sm font-medium">Points earned per AED spent</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.earn_rate}
                onChange={(event) => updateField('earn_rate', event.target.value)}
                className={inputClass}
                disabled={!form.enabled}
              />
              <span className="text-xs text-muted-foreground">
                Example: AED 150 bill earns {Number.isFinite(earnPreview) ? Math.floor(150 * earnPreview) : '—'} points.
              </span>
            </label>

            <label className="block space-y-1.5">
              <span className="text-sm font-medium">AED value per point (redeem)</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.redeem_rate}
                onChange={(event) => updateField('redeem_rate', event.target.value)}
                className={inputClass}
                disabled={!form.enabled}
              />
              <span className="text-xs text-muted-foreground">
                Example: 100 points = AED {Number.isFinite(redeemPreview) ? (100 * redeemPreview).toFixed(2) : '—'} off.
              </span>
            </label>
          </div>

          <label className="block space-y-1.5">
            <span className="text-sm font-medium">Minimum points to redeem</span>
            <input
              type="number"
              min="0"
              step="1"
              value={form.min_redeem_points}
              onChange={(event) => updateField('min_redeem_points', event.target.value)}
              className={cn(inputClass, 'max-w-xs')}
              disabled={!form.enabled}
            />
            <span className="text-xs text-muted-foreground">
              Guests need at least {Number.isFinite(minPreview) ? minPreview : '—'} points before they can redeem on POS.
            </span>
          </label>

          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}
          {success ? <p className="text-sm text-emerald-700">{success}</p> : null}

          <Button type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                Saving…
              </>
            ) : (
              'Save loyalty settings'
            )}
          </Button>
        </form>
      )}
    </SettingsSection>
  )
}
